/**
 * The public landing page, top to bottom:
 *
 * 1. Hero: the problem in the reader's words, one action (copy the install command),
 *    and two terminals running one prompt, with the latest campaign's measured figures
 *    under them (TwoTerminals).
 * 2. Field numbers: public install and savings totals, shown only once they are real.
 * 3. Proof: the paired-run ledger, every row including the ties.
 * 4. Security: what stays on the machine and what reaches Archis.
 * 5. Plans: what a lead pays for (right the first time, set up on clone), then Free and
 *    Team, prices from the catalogue, stated once each.
 * 6. The close: the install action again, on the hero's grid.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { LANGUAGES } from '@/entities/languages'
import { AGENT_LOOP, FIRST_TIME_RIGHT, INDEXING_GATE as gate } from '@/entities/metrics'
import type { TelemetryStats } from '@/entities/types'
import { planPricing } from '@/entities/pricing'
import { useCatalog, useTelemetryStats } from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { useSeenOnce } from '@/shared/lib/useSeenOnce'
import { Footer, Masthead } from '@/shared/ui/Chrome'
import {
  ArrowRightIcon,
  BotIcon,
  CheckIcon,
  CopyIcon,
  ShieldCheckIcon,
  TerminalIcon,
} from '@/shared/ui/icons'
import { Label, Notice, formatPrice } from '@/shared/ui/primitives'
import { TwoTerminals } from '@/shared/ui/TwoTerminals'

export function Landing() {
  // A one-time line a redirect can carry — the account page sends people here
  // after deleting their account, and the landing is the only page they can
  // still reach. Router state, not a query parameter, so a reload or a shared
  // link does not repeat it.
  const notice = (useLocation().state as { notice?: string } | null)?.notice
  return (
    <>
      <Masthead />
      {notice && (
        <div className="container" style={{ paddingTop: 'var(--s4)' }}>
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}
      <main id="main" className="landing">
        <Hero />
        <FieldNumbers />
        <LiveSimulationAndProof />
        <SecurityGuarantee />
        <Plans />
        <FinalCta />
      </main>
      <Footer />
    </>
  )
}

/* -------------------------------------------------------------------- 1. Hero --- */

interface InstallMode {
  id: string
  label: string
  icon: ReactNode
  command: string
  promptSymbol: string
  /** Under the command in the hero: what runs, and the one thing the terminal will ask. */
  hint: ReactNode
  /** Under the command at the close: shorter, for a reader who has had the rest. */
  short: ReactNode
}

const INSTALL_MODES: InstallMode[] = [
  {
    id: 'terminal',
    label: 'In your terminal',
    icon: <TerminalIcon />,
    command: 'curl -fsSL https://archis.dev/install.sh | sh && chi init',
    promptSymbol: '$',
    hint: 'Installs the CLI, indexes this repository, connects your agents. You sign in once, with GitHub or Google, when it asks.',
    short: 'Indexes this repository and connects your agents. Sign in once when it asks.',
  },
  {
    id: 'agent',
    label: 'Ask your agent',
    icon: <BotIcon />,
    command: 'Set Archis up in this repository: read https://archis.dev/skill.md and follow it.',
    promptSymbol: '❯',
    hint: (
      <>
        Paste into Claude Code, Cursor, Codex or Antigravity. The agent does the install, following{' '}
        <a href="/skill.md" className="signal">archis.dev/skill.md</a>; you sign in once when it asks.
      </>
    ),
    short: (
      <>
        The agent does the install from <a href="/skill.md" className="signal">archis.dev/skill.md</a>. Sign in once when it asks.
      </>
    ),
  },
]

const DEFAULT_INSTALL_MODE: InstallMode = INSTALL_MODES[0]!

/**
 * The one action, in one place in the code: the hero shows it first and the close
 * shows it last, on the same grid. "Install", wherever it is on the site, means
 * bring me to the nearest command: arriving at `/#<id>` scrolls the box into view,
 * outlines it for a second, and puts focus on Copy so Enter copies. The install
 * page is for people who want the binaries.
 */
function InstallBox({ id, place }: { id: string; place: 'hero' | 'close' }) {
  const [activeModeId, setActiveModeId] = useState('terminal')
  const [copied, setCopied] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const copyButton = useRef<HTMLButtonElement>(null)
  const { hash } = useLocation()

  useEffect(() => {
    if (hash !== `#${id}` || !box.current) return undefined
    box.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    box.current.classList.add('install-box--here')
    copyButton.current?.focus({ preventScroll: true })
    const t = setTimeout(() => box.current?.classList.remove('install-box--here'), 1400)
    return () => clearTimeout(t)
  }, [hash, id])

  const activeMode: InstallMode =
    INSTALL_MODES.find((opt) => opt.id === activeModeId) ?? DEFAULT_INSTALL_MODE

  function copyCommand() {
    navigator.clipboard.writeText(activeMode.command)
    setCopied(true)
    capture('landing_install_command_copied', { mode: activeMode.id, place })
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="install-box install-switcher reveal" id={id} ref={box}>
      <div className="install-tabs" role="tablist" aria-label="Install mode">
        {INSTALL_MODES.map((tab) => {
          const isActive = tab.id === activeMode.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`install-tab ${isActive ? 'is-active' : ''}`}
              onClick={() => {
                setActiveModeId(tab.id)
                setCopied(false)
                capture('landing_install_tab_selected', { tab: tab.id, place })
              }}
            >
              <span className="install-tab__icon" aria-hidden="true">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      <div className="install-command-row">
        <div className="install-command-code">
          <span className="term__prompt" style={{ fontWeight: 700, color: 'var(--signal)', flexShrink: 0 }}>
            {activeMode.promptSymbol}
          </span>
          <code>{activeMode.command}</code>
        </div>

        <button
          type="button"
          onClick={copyCommand}
          className="install-copy-btn"
          aria-label={`Copy ${activeMode.promptSymbol === '❯' ? 'prompt' : 'command'}`}
          ref={copyButton}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>

      <div className="install-footer">
        <span className="mono-note">
          {place === 'hero' ? (
            <>
              {activeMode.hint} No endpoint accepts source (<Link to="/privacy">what leaves your machine</Link>).
            </>
          ) : (
            activeMode.short
          )}
        </span>
      </div>
    </div>
  )
}

function Hero() {
  return (
    <section className="hero-modern">
      <div className="aurora" aria-hidden="true" />
      <div className="grid-veil" aria-hidden="true" />

      <div className="shell">
        <div className="hero-grid">
          {/* Row 1, left: the problem and what Archis is. */}
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <h1 className="reveal hero-title">Stop burning agent context on whole-file dumps.</h1>
            <p className="lead reveal hero-lead">The local index your agent asks before it reads a file.</p>
          </div>

          {/* Row 1, right: the one action. */}
          <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
            <InstallBox id="install" place="hero" />
            <p className="hero-agents reveal">
              Works with Claude Code, Cursor, Codex CLI, Gemini CLI, Antigravity and any MCP client.
            </p>
          </div>
        </div>

        {/* Row 2: two terminals, one prompt. */}
        <div className="hero-instrument reveal">
          <TwoTerminals />
        </div>
      </div>
    </section>
  )
}

/* --------------------------------------------------------- Field numbers --- */

/** Compact form: 12,400 → 12.4K, 3,200,000 → 3.2M. */
function compact(value: number): string {
  const units: Array<[number, string]> = [
    [1_000_000_000, 'B'],
    [1_000_000, 'M'],
    [1_000, 'K'],
  ]
  for (const [size, suffix] of units) {
    if (value >= size) {
      const scaled = value / size
      // One decimal below ten, none above: "1.2M" reads, "12.4M" is noise.
      return `${scaled < 10 ? scaled.toFixed(1) : Math.round(scaled)}${suffix}`
    }
  }
  return String(value)
}

/** Whether the aggregate has anything worth a band: any reach figure above zero. */
function hasFigures(stats: TelemetryStats): boolean {
  return stats.repositories > 0 || stats.installs > 0 || stats.tokens_saved > 0
}

/**
 * What the fleet actually did, from opted-in telemetry.
 *
 * Rendered only once real numbers arrive. Zeroes on a failed request, or from a
 * deployment nobody has reported to yet, are worse than no section at all — so
 * the absent state here is the section not existing, and it appears as soon as
 * the API answers with at least one non-zero figure. The query behind it refetches
 * on the server's own recompute cadence, so the numbers move without a reload.
 *
 * Every figure is a floor and the footnote says so. Measurements travel as ranges
 * so that no individual install is identifiable from them, and adding the bottom
 * of each range gives a number that understates by construction. That is the only
 * honest way to put bucketed data on a marketing page.
 */
function FieldNumbers() {
  const { data } = useTelemetryStats()
  if (!data || !hasFigures(data)) return null

  // Three totals and no ratio. The modal reduction bucket is a claim about a
  // per-process share, built from far fewer reports than the counts are, and a
  // headline figure has to be the one nobody can argue with.
  const figures = [
    { value: compact(data.tokens_saved), label: 'Tokens saved' },
    { value: compact(data.repositories), label: 'Repositories indexed' },
    { value: data.installs >= 100 ? compact(data.installs) : String(LANGUAGES.length), label: data.installs >= 100 ? 'Installations' : 'Languages supported' },
  ]

  return (
    <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
      <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
        <div className="grid-3" style={{ gap: 'var(--s4)' }}>
          {figures.map((figure) => (
            <div
              key={figure.label}
              className="card stack"
              style={{ ['--gap' as string]: 'var(--s2)', padding: 'var(--s5)', textAlign: 'center' }}
            >
              <div
                className="metric__value"
                style={{ fontSize: '2rem', fontFamily: 'var(--mono)', color: 'var(--signal)' }}
              >
                {figure.value}
              </div>
              <div className="dim" style={{ fontSize: '0.875rem' }}>
                {figure.label}
              </div>
            </div>
          ))}
        </div>
        <p className="mono-note" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          Opted-in anonymous telemetry. Measurements are lower bounds. There is no source endpoint. <Link to="/privacy#telemetry" className="signal">Telemetry details</Link>
        </p>
      </div>
    </section>
  )
}

/* ------------------------------------------------ 3. Problem & Solution (Before vs After) --- */

/* ------------------------------------------- 5. Live Simulation & Benchmark Proof --- */

/**
 * The ledger: every row of the one measurement we trust most, in the same type,
 * including the two that are level. A reader who finds the loss themselves stops
 * believing the wins; a reader handed it believes both. The figures are
 * `AGENT_LOOP` in metrics.ts; the verdict column is the plain-English reading of
 * whether the ranges overlap, which is the only statistics the page carries.
 */
const VERDICT: Record<string, string> = {
  'Output the model wrote': 'separates completely',
  'Input tokens': 'ranges overlap',
  'Cost per run': 'ranges overlap',
  'Wall clock': 'ranges overlap',
  'Reasoning tokens': 'slightly against us',
  'Answer accuracy': 'the saving is not bought with quality',
}

function LiveSimulationAndProof() {
  return (
    <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
          <Label>Measured</Label>
          <h2 style={{ maxWidth: '30ch' }}>
            {AGENT_LOOP.pairs} paired runs: one repository, one model, the same prompt.
          </h2>
          <p className="lead" style={{ maxWidth: '56ch' }}>
            An earlier Codex CLI run, priced in money. Every row, including two ties.
          </p>
        </div>

        <div className="ledger-wrap">
          <table className="ledger">
            <thead>
              <tr>
                <th scope="col">Measure</th>
                <th scope="col" className="num">Reading files</th>
                <th scope="col" className="num">With Archis</th>
                <th scope="col">Difference</th>
                <th scope="col">How sure</th>
              </tr>
            </thead>
            <tbody>
              {AGENT_LOOP.rows.map((row) => (
                <tr key={row.measure} className={row.win ? '' : 'ledger__row--level'} title={row.note}>
                  <th scope="row">{row.measure}</th>
                  <td className="num" data-label="reading files ">{row.control}</td>
                  <td className="num" data-label="with Archis ">{row.chi}</td>
                  <td className={`ledger__delta${row.win ? '' : ' ledger__delta--level'}`}>{row.delta}</td>
                  <td className="ledger__verdict">
                    {row.separated && <span className="ledger__dot" aria-hidden="true" />}
                    {VERDICT[row.measure]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ledger__foot">
          <p>
            Archis 0.18.0 · OpenAI Codex CLI 0.138.0 with gpt-5.5, effort high · prometheus at{' '}
            <code>e6cfa720</code> · {AGENT_LOOP.pairs} pairs across 3 campaigns. Every figure is a mean over the pairs
            with its range beside it in the source; only the first row separates completely.
          </p>
          <p>
            Index tested on {gate.repos} pinned public repositories, {gate.files} files,{' '}
            {gate.deterministicInWords} · recall ≥0.90 at a 1,600-token budget on 18 of 20 repositories.{/* claims-ok(token-budget): the per-query retrieval budget, not a spend budget */}
          </p>
          <p>
            <Link to="/whitepaper" className="signal">
              All the tables, with sources
            </Link>
          </p>
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------- 6. Security Guarantee --- */

function SecurityGuarantee() {
  return (
    <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
          <Label>What leaves your machine</Label>
          <h2 style={{ maxWidth: '30ch' }}>Your code never leaves your machine.</h2>
          <p className="lead" style={{ maxWidth: '56ch' }}>
            Parsing, indexing and agent queries run on your machine. There is no source endpoint.
          </p>
        </div>

      <div className="glass" style={{ padding: 'var(--s6)' }}>
        <div className="grid-2" style={{ gap: 'var(--s7)', alignItems: 'center' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>

            <ul className="feature-list" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)', margin: 0 }}>
              {[
                'No source endpoint — the API does not accept source',
                'No repository names or file paths transmitted',
                'Sign-in is used only to verify the licence',
                'Indexing and MCP run on each developer’s machine, on every plan',
              ].map((item) => (
                <li key={item} className="feature-item" style={{ alignItems: 'flex-start', gap: 'var(--s3)' }}>
                  <ShieldCheckIcon className="signal" style={{ marginTop: '0.25rem', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>

            <Link to="/privacy#telemetry" className="cta" style={{ width: 'fit-content' }}>
              <span>Read privacy disclosure</span>
              <ArrowRightIcon />
            </Link>
          </div>

          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="boundary">
              <div className="boundary__tag">Your machine (100% Local)</div>
              <div className="boundary__flow">
                <span className="boundary__node">Source code</span>
                <span className="boundary__arrow">→</span>
                <span className="boundary__node">tree-sitter</span>
                <span className="boundary__arrow">→</span>
                <span className="boundary__node signal">local index</span>
                <span className="boundary__arrow">→</span>
                <span className="boundary__node">MCP stdio</span>
                <span className="boundary__arrow">→</span>
                <span className="boundary__node">Coding Agent</span>
              </div>
            </div>
            <div className="row mono-note" style={{ gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="boundary__arrow">↳</span>
              <span>Only account licensing &amp; team policies reach the cloud</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ Plans --- */

/**
 * Two cards: your own repositories, and a team's. The price is stated once per card,
 * as a fact, from the catalogue the API serves; nothing here reassures ("forever", "no
 * credit card"), because a page that keeps saying free sounds like it is about to
 * charge (VOICE.md §5). Enterprise and anything custom is one line pointing at the
 * pages that can answer properly.
 */
function Plans() {
  const { data: catalog } = useCatalog()
  const teamPlan = catalog?.plans?.find((p) => p.code === 'team')
  const monthly = teamPlan ? planPricing(teamPlan, 'monthly') : null
  const annual = teamPlan ? planPricing(teamPlan, 'annual', catalog?.annual_discount_percent) : null
  const seats =
    teamPlan?.min_paid_seats && teamPlan.max_paid_seats
      ? `${teamPlan.min_paid_seats} to ${teamPlan.max_paid_seats} seats`
      : '3 to 200 seats'

  return (
    <section className="shell" id="pricing" style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '5rem' }}>
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
          <Label>Plans</Label>
          <h2 style={{ maxWidth: '30ch' }}>Your repositories, or your team’s.</h2>
        </div>

        <FirstTimeRight />

        <div className="plans">
          <div className="plan">
            <div className="plan__head">
              <h3>Your repositories</h3>
              <div className="plan__price">
                <span className="plan__amount">$0</span>
              </div>
            </div>
            <ul className="plan__list">
              <li>The CLI, the local index and the MCP server</li>
              <li>Every agent it finds, connected by <code>chi init</code></li>
              <li>All stable languages and integrations</li>
              <li>Two active devices</li>
            </ul>
            <Link to="/#install-close" className="cta" onClick={() => capture('landing_plan_cta_clicked', { plan: 'free' })}>
              Install
            </Link>
          </div>

          <div className="plan plan--team">
            <div className="plan__head">
              <h3>Your team</h3>
              <div className="plan__price">
                <span className="plan__amount">{monthly ? formatPrice(monthly.chargedCents) : '$10'}</span>
                <span className="plan__per"> a seat, a month</span>
              </div>
              <div className="plan__note">
                {annual ? `${formatPrice(annual.perMonthCents)} if billed yearly` : '$8 if billed yearly'} · {seats}
              </div>
            </div>
            <ul className="plan__list">
              <li>Organisation repositories</li>
              <li>Agent settings committed in the repo, shared by the team</li>
              <li>Seats pooled and reassigned from the portal</li>
              <li>Policies and an audit log</li>
            </ul>
            <Link to="/pricing" className="cta" onClick={() => capture('landing_plan_cta_clicked', { plan: 'team' })}>
              See what is included
            </Link>
          </div>
        </div>

        <p className="mono-note" style={{ margin: 0 }}>
          SSO and SCIM, audit streaming or an offline licence, at any team size:{' '}
          <Link to="/enterprise" className="signal">
            talk to us
          </Link>
          .
        </p>
      </div>
    </section>
  )
}

/**
 * What a lead pays for, in their currency: senior hours not spent repairing what an
 * agent did in old code. Quality first, consistency second, set-up-on-clone as the
 * team hook (#1162). Tokens, the proof for teams on an API key, stay on /pricing,
 * one click away: this page has a word budget. The two measured figures come from
 * different runs and each carries its own scope.
 */
function FirstTimeRight() {
  const seen = useSeenOnce<HTMLDivElement>('first_time_right_viewed', { page: 'landing' })
  const { quality, consistency } = FIRST_TIME_RIGHT
  const figures = [
    { value: `${quality.without.toFixed(2)} to ${quality.with.toFixed(2)}`, label: 'Answer score, without and with Archis' },
    { value: `${consistency.archis}% vs ${consistency.native}%`, label: 'Run-to-run spread in what the model writes, with Archis and without' },
    { value: 'On clone', label: 'Agent settings committed, the same for everyone' },
  ]

  return (
    <div className="stack" ref={seen} style={{ ['--gap' as string]: 'var(--s4)' }}>
      <p className="lead" style={{ maxWidth: '60ch', margin: 0 }}>
        For a team the cost is rework: a senior engineer repairing an edit the agent made without knowing the callers.
      </p>
      <div className="grid-3" style={{ gap: 'var(--s4)' }}>
        {figures.map((figure) => (
          <div key={figure.label} className="card stack" style={{ ['--gap' as string]: 'var(--s2)', padding: 'var(--s5)' }}>
            <div className="metric__value" style={{ fontSize: '1.5rem', fontFamily: 'var(--mono)', color: 'var(--signal)' }}>
              {figure.value}
            </div>
            <div className="dim" style={{ fontSize: '0.875rem' }}>
              {figure.label}
            </div>
          </div>
        ))}
      </div>
      <p className="mono-note" style={{ margin: 0, lineHeight: 1.6 }}>
        Score: {quality.short}. Spread: {consistency.short}. One repository each.{' '}
        <Link to="/verify" className="signal">
          How it was measured
        </Link>
      </p>
    </div>
  )
}

/* --------------------------------------------------------------- The close --- */

/**
 * The close is a bookend: the hero's grid again, the label naming the one action,
 * one sentence that says the thing the page has not said yet (run it inside the
 * repository, since `chi init` indexes the one you are in), and the same box.
 */
function FinalCta() {
  return (
    <section className="shell" style={{ paddingBottom: 'var(--s9)' }}>
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <Label>Install</Label>
        <div className="hero-grid close-grid">
          <h2 className="close-title">Run it in the repository your agent keeps re-reading.</h2>
          <InstallBox id="install-close" place="close" />
        </div>
      </div>
    </section>
  )
}
